'use client';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import {
  CountryField,
  TextField,
} from '@eliadmin/core/src/components/dlz/auth/register/register-fields';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

type AddressFieldNames = {
  street: 'address' | 'delivery_address';
  city: 'city' | 'delivery_city';
  postcode: 'postcode' | 'delivery_postcode';
  country: 'country' | 'delivery_country';
};

export const BILLING_FIELDS: AddressFieldNames = {
  street: 'address',
  city: 'city',
  postcode: 'postcode',
  country: 'country',
};

export const DELIVERY_FIELDS: AddressFieldNames = {
  street: 'delivery_address',
  city: 'delivery_city',
  postcode: 'delivery_postcode',
  country: 'delivery_country',
};

/**
 * The address rows of the registration (Figma 60260:29707, 60260:30242): street and number, city
 * (flexible) next to the 260 px postcode, country. Required for the delivery address only.
 */
export function AddressFields({
  fields,
  countries,
  required = false,
  autoCompletePrefix = '',
}: {
  fields: AddressFieldNames;
  countries: Country[];
  required?: boolean;
  /** Section token of the autofill hint, e.g. „billing “. */
  autoCompletePrefix?: string;
}) {
  const { t } = useTranslations();

  return (
    <div className="flex flex-col gap-4">
      <TextField
        name={fields.street}
        label={REGISTER_COPY.address.street}
        required={required}
        autoComplete={`${autoCompletePrefix}address-line1`}
        placeholder={REGISTER_COPY.address.street}
      />
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_260px]">
        <TextField
          name={fields.city}
          label={t('text39')}
          required={required}
          autoComplete={`${autoCompletePrefix}address-level2`}
          placeholder={t('text39')}
        />
        <TextField
          name={fields.postcode}
          label={REGISTER_COPY.address.postcode}
          required={required}
          autoComplete={`${autoCompletePrefix}postal-code`}
          placeholder={REGISTER_COPY.address.postcodePlaceholder}
        />
      </div>
      <CountryField
        name={fields.country}
        label={t('text519')}
        required={required}
        countries={countries}
        autoComplete={`${autoCompletePrefix}country`}
      />
    </div>
  );
}
